/**
 * AI Configuration API Client
 * Handles all AI provider configuration operations
 */

import { apiClient } from './client';

export type AIProvider = 'openai' | 'anthropic' | 'groq' | 'together' | 'ica-nextgen' | 'ica-global';

export interface AIConfiguration {
  id: string;
  userId: string;
  provider: AIProvider;
  modelId: string;
  apiKey?: string; // Only included when explicitly requested
  isActive: boolean;
  isDefault: boolean;
  displayName?: string;
  description?: string;
  // Optional OpenAI-compatible base URL override (e.g. a Databricks model-serving endpoint).
  baseUrl?: string;
  settings: Record<string, any>;
  lastUsedAt?: string;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAIConfigRequest {
  provider: AIProvider;
  modelId: string;
  apiKey: string;
  displayName?: string;
  description?: string;
  baseUrl?: string;
  settings?: Record<string, any>;
  isDefault?: boolean;
}

export interface UpdateAIConfigRequest {
  modelId?: string;
  apiKey?: string;
  displayName?: string;
  description?: string;
  baseUrl?: string;
  settings?: Record<string, any>;
  isActive?: boolean;
  isDefault?: boolean;
}

// ── Override status ───────────────────────────────────────────────────────────

export interface LlmOverrideStatus {
  overrideActive: boolean;
  displayName: string | undefined;
  provider: string | undefined;
  baseUrl: string | undefined;
  models: {
    large: string | undefined;
    medium: string | undefined;
    small: string | undefined;
    vlm: string | undefined;
  };
}

/**
 * AI Configuration API
 */
export const aiConfigApi = {
  /**
   * Get all AI configurations for the current user
   */
  async getConfigurations(): Promise<AIConfiguration[]> {
    return apiClient.get<AIConfiguration[]>('/api/ai-configs');
  },

  /**
   * Get a specific AI configuration by ID
   */
  async getConfiguration(id: string): Promise<AIConfiguration> {
    return apiClient.get<AIConfiguration>(`/api/ai-configs/${id}`);
  },

  /**
   * Get the default AI configuration
   */
  async getDefaultConfiguration(): Promise<AIConfiguration> {
    return apiClient.get<AIConfiguration>('/api/ai-configs/default/current');
  },

  /**
   * Create a new AI configuration
   */
  async createConfiguration(data: CreateAIConfigRequest): Promise<AIConfiguration> {
    return apiClient.post<AIConfiguration>('/api/ai-configs', data);
  },

  /**
   * Update an AI configuration
   */
  async updateConfiguration(
    id: string,
    data: UpdateAIConfigRequest
  ): Promise<AIConfiguration> {
    return apiClient.patch<AIConfiguration>(`/api/ai-configs/${id}`, data);
  },

  /**
   * Delete an AI configuration
   */
  async deleteConfiguration(id: string): Promise<void> {
    await apiClient.delete(`/api/ai-configs/${id}`);
  },

  /**
   * Test if an API key is valid
   */
  async testApiKey(id: string): Promise<boolean> {
    const result = await apiClient.post<{ isValid: boolean }>(`/api/ai-configs/${id}/test`);
    return result.isValid;
  },

  /**
   * Test API key and fetch available models (before saving configuration).
   * @param baseUrl Optional custom endpoint — required for ica-nextgen / ica-global
   *                when the user's instance is not at the default URL.
   */
  async testAndFetchModels(
    provider: AIProvider,
    apiKey: string,
    baseUrl?: string
  ): Promise<{ isValid: boolean; models: string[]; error?: string }> {
    const result = await apiClient.post<{
      isValid: boolean;
      models: string[];
      provider: AIProvider;
      error?: string;
    }>('/api/ai-configs/test-and-fetch-models', {
      provider,
      apiKey,
      ...(baseUrl?.trim() && { baseUrl: baseUrl.trim() }),
    });
    return result;
  },

  /**
   * Fetch available models for an existing config (uses stored API key)
   */
  async fetchModelsForConfig(configId: string): Promise<{ models: string[]; error?: string }> {
    const result = await apiClient.get<{
      models: string[];
      provider: AIProvider;
      error?: string;
    }>(`/api/ai-configs/${configId}/models`);
    return result;
  },

  /**
   * Update usage statistics for a configuration
   */
  async updateUsage(id: string): Promise<void> {
    await apiClient.post(`/api/ai-configs/${id}/usage`);
  },

  /**
   * Get the platform-global LLM override status.
   * When overrideActive=true the per-user AI Configuration UI should be
   * replaced by a read-only "Platform Global Setting" banner.
   */
  async getOverrideStatus(): Promise<LlmOverrideStatus> {
    return apiClient.get<LlmOverrideStatus>('/api/ai-config/status');
  },
};

/**
 * Provider display names
 */
export const PROVIDER_NAMES: Record<AIProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  groq: 'Groq',
  together: 'Together AI',
  'ica-nextgen': 'ICA Next-Gen',
  'ica-global': 'ICA Global (IBM Services Essentials)',
};

/**
 * Provider descriptions
 */
export const PROVIDER_DESCRIPTIONS: Record<AIProvider, string> = {
  openai: 'GPT-4, GPT-3.5, and other OpenAI models',
  anthropic: 'Claude 3 and other Anthropic models',
  groq: 'Fast inference with Llama, Mixtral, and more',
  together: 'Open-source models with high performance',
  'ica-nextgen': 'IBM Cloud Automation Next-Gen AI',
  'ica-global': 'IBM Services Essentials (api.servicesessentials.ibm.com) — OpenAI-compatible',
};

/**
 * Default model IDs for each provider
 */
export const DEFAULT_MODELS: Record<AIProvider, string> = {
  openai: 'gpt-4-turbo-preview',
  anthropic: 'claude-3-opus-20240229',
  groq: 'mixtral-8x7b-32768',
  together: 'meta-llama/Llama-3-70b-chat-hf',
  'ica-nextgen': 'ibm/granite-13b-chat-v2',
  'ica-global': 'ibm/granite-13b-chat-v2',
};

// Made with Bob
